import React from 'react';

interface CardDataStatsProps {
  title: string;
  total: string;
  rate?: string;
  levelUp?: boolean;
  levelDown?: boolean;
  subText?: string;
  badgeText?: string;
  badgeColor?: 'green' | 'blue' | 'yellow' | 'red';
  children: React.ReactNode;
}

export default function CardDataStats({
  title,
  total,
  rate,
  levelUp,
  levelDown,
  subText,
  badgeText,
  badgeColor = 'green',
  children,
}: CardDataStatsProps) {
  return (
    <div className="rounded-2.5xl border border-brand-line bg-white p-5 sm:p-6 shadow-card dark:border-strokedark dark:bg-boxdark">
      {/* Top Row: Icon & Trend / Badge */}
      <div className="flex items-center justify-between">
        <div className="flex h-11.5 w-11.5 items-center justify-center rounded-xl bg-brand-bg dark:bg-strokedark/50">
          {children}
        </div>

        {rate && (
          <span
            className={`flex items-center gap-1 text-xs font-mono font-semibold px-2 py-0.5 rounded-full ${
              levelDown
                ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300'
                : levelUp
                ? 'text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300'
                : 'text-brand-vib bg-brand-blue-tint dark:bg-brand-900/40 dark:text-brand-300'
            }`}
          >
            {rate}
            {levelDown && (
              <svg className="w-3 h-3 fill-current" viewBox="0 0 10 10">
                <path d="M5 8.5L1.5 4.5h7L5 8.5z" />
              </svg>
            )}
            {levelUp && (
              <svg className="w-3 h-3 fill-current" viewBox="0 0 10 10">
                <path d="M5 1.5l3.5 4h-7L5 1.5z" />
              </svg>
            )}
          </span>
        )}

        {badgeText && (
          <span
            className={`text-[10px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              badgeColor === 'green'
                ? 'bg-brand-green-tint text-[#0a6b4a] dark:bg-emerald-950/40 dark:text-emerald-300 border border-brand-green/30'
                : 'bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300'
            }`}
          >
            {badgeText}
          </span>
        )}
      </div>

      {/* Main Metric Value */}
      <div className="mt-4 flex items-end justify-between">
        <div>
          <span className="text-[11px] font-mono font-medium uppercase tracking-wider text-brand-soft dark:text-bodydark2">
            {title}
          </span>
          <h2 className="text-2xl sm:text-3xl font-mono font-bold text-brand-navy dark:text-white mt-0.5">
            {total}
          </h2>
          {subText && (
            <p className="text-[11px] text-brand-muted dark:text-bodydark mt-1">
              {subText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
